import Pannellum from 'lib/pannellum';
import PannellumAdmin from '../../pannellum-poi-editor/pannellum-admin';
import RenderZoneManager from './service';
import ModalService from '../service/modal/e3.viewer.modal.service';
import E3Drag from 'lib/helpers/draggable';
import { generateUUID } from 'lib/helpers/common';

const MAX_POINTS = 4;

export default {
    _renderZoneEditorMode: false,
    _renderZonePoints: [],
    _renderZoneCloseElements: [],
    _waitingRenderZonePoint: false,

    _onDragRenderZonePoint(element, px, py) {
        const [pitch, yaw] = this._mouseEventToCoords({ clientX: px, clientY: py });
        const index = this._renderZonePoints.findIndex(pair => pair.includes(element));
        this._renderZoneManager.updateVertexView([yaw, pitch], index);
        this._render();
    },

    renderZoneRender() {
        if (this._loaded && this._renderZoneEditorMode) {
            this._renderRenderZonePoints();
        }
    },

    renderZoneFinishInteracting({ eventObjects }) {
        if (!this._waitingRenderZonePoint) return;
        const { x, y } = this._mousePosition(eventObjects[0]);
        const dx = x - this._interactionData.x;
        const dy = y - this._interactionData.y;
        if (Math.sqrt(dx * dx + dy * dy) < 4) {
            const [pitch, yaw] = this._mouseEventToCoords(eventObjects[0]);
            this._renderInteractingRenderZonePoint(yaw, pitch);
            this._render();
        }
    },

    renderZoneContinueInteracting(eventObjects) {
        if (this._waitingRenderZonePoint && !this._isUserInteracting) {
            if (eventObjects[0].target === this._dragFix) {
                const [pitch, yaw] = this._mouseEventToCoords(eventObjects[0]);
                this._renderZoneManager.setGhostPoint([yaw, pitch]);
            } else {
                this._renderZoneManager.removeGhostPoint();
            }
            if (!this._renderer.isLoading()) requestAnimationFrame(this._animateBound);
        }
    },

    setRenderZoneData(render_zones_json) {
        this._destroyRenderZonePoints();
        this._removeAllRenderZoneCloseIcons();
        if (!this._renderZoneEditorMode || !render_zones_json) return;
        this._renderZoneManager.setZones(JSON.parse(render_zones_json));
        this._createRenderZonePlan();
    },

    _createRenderZonePlan() {
        this._renderRenderZonePoints();
        this._showRenderZoneCounter();
    },

    _renderInteractingRenderZonePoint(yaw, pitch) {
        this._renderZoneManager.addPoint([yaw, pitch]);
        this._renderZonePoints.push(this._createRenderZonePointEl());

        if (this._renderZoneManager.getVertexCount() >= MAX_POINTS) {
            this._closeRenderZone();
        }
    },

    async _closeRenderZone() {
        const uuid = generateUUID();
        const media = await this._promptZoneMedia();
        if (!media) {
            this._renderZoneManager.removeCurrentArea();
            this._destroyRenderZonePoints();
            return;
        }
        const zone = this._renderZoneManager.pushToZone(uuid);
        this._renderZoneManager.setZoneMedia(uuid, media.url, media.type);
        this._destroyRenderZonePoints();
        this._render();
        this._showRenderZoneCounter();
        console.log('[RenderZone] _closeRenderZone: firing render-zone-added on PannellumAdmin', zone);
        this._fireEvent('render-zone-added', zone);
    },

    _promptZoneMedia() {
        return new Promise(resolve => {
            const modalService = new ModalService(this._container, this._translateService);
            let activeTab = 'video';

            const wrap = document.createElement('div');
            wrap.classList.add('add-floor-modal', 'vz-media-modal');

            // --- tabs ---
            const tabs = document.createElement('div');
            tabs.classList.add('vz-tabs');

            const tabVideo = document.createElement('button');
            tabVideo.textContent = 'Video';
            tabVideo.classList.add('vz-tab', 'active');

            const tabImage = document.createElement('button');
            tabImage.textContent = 'Image';
            tabImage.classList.add('vz-tab');

            tabs.appendChild(tabVideo);
            tabs.appendChild(tabImage);

            // --- video panel ---
            const videoPanel = document.createElement('div');
            videoPanel.classList.add('vz-panel');

            const videoLabel = document.createElement('p');
            videoLabel.textContent = 'YouTube or Vimeo URL:';

            const videoInput = document.createElement('input');
            videoInput.type = 'text';
            videoInput.placeholder = 'https://www.youtube.com/watch?v=...';

            videoPanel.appendChild(videoLabel);
            videoPanel.appendChild(videoInput);

            // --- image panel ---
            const imagePanel = document.createElement('div');
            imagePanel.classList.add('vz-panel');
            imagePanel.style.display = 'none';

            const imageLabel = document.createElement('p');
            imageLabel.textContent = 'Image URL or upload:';

            const imageInput = document.createElement('input');
            imageInput.type = 'text';
            imageInput.placeholder = 'https://example.com/image.jpg';

            const uploadRow = document.createElement('div');
            uploadRow.classList.add('vz-upload-row');

            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = 'image/*';
            fileInput.style.display = 'none';

            const uploadBtn = document.createElement('button');
            uploadBtn.textContent = 'Upload file';
            uploadBtn.classList.add('btn', 'eyespy-btn-secondary');
            uploadBtn.style.cssText = 'margin-top:6px;font-size:12px;padding:4px 10px;';
            uploadBtn.onclick = (e) => { e.preventDefault(); fileInput.click(); };

            const uploadStatus = document.createElement('span');
            uploadStatus.classList.add('vz-upload-status');

            fileInput.onchange = () => {
                const file = fileInput.files && fileInput.files[0];
                if (!file) return;
                if (file.size > 25 * 1024 * 1024) {
                    uploadStatus.textContent = 'File too large (max 25 MB)';
                    fileInput.value = '';
                    return;
                }
                uploadStatus.textContent = 'Uploading…';
                uploadBtn.disabled = true;
                btnOk.disabled = true;
                const onSuccess = (url) => {
                    imageInput.value = url;
                    uploadStatus.textContent = '';
                    uploadBtn.disabled = false;
                    btnOk.disabled = false;
                };
                const onError = () => {
                    uploadStatus.textContent = 'Upload failed';
                    uploadBtn.disabled = false;
                    btnOk.disabled = false;
                };
                this._fireEvent('render-zone-image-upload', { file, onSuccess, onError });
            };

            uploadRow.appendChild(uploadBtn);
            uploadRow.appendChild(fileInput);
            uploadRow.appendChild(uploadStatus);

            imagePanel.appendChild(imageLabel);
            imagePanel.appendChild(imageInput);
            imagePanel.appendChild(uploadRow);

            // --- tab switching ---
            tabVideo.onclick = () => {
                activeTab = 'video';
                tabVideo.classList.add('active');
                tabImage.classList.remove('active');
                videoPanel.style.display = '';
                imagePanel.style.display = 'none';
                setTimeout(() => videoInput.focus(), 10);
            };
            tabImage.onclick = () => {
                activeTab = 'image';
                tabImage.classList.add('active');
                tabVideo.classList.remove('active');
                videoPanel.style.display = 'none';
                imagePanel.style.display = '';
                setTimeout(() => imageInput.focus(), 10);
            };

            // --- actions ---
            const actions = document.createElement('div');
            actions.classList.add('variant');
            actions.style.marginTop = '14px';

            const btnOk = document.createElement('button');
            btnOk.textContent = 'OK';
            btnOk.onclick = () => {
                const url = (activeTab === 'video' ? videoInput.value : imageInput.value).trim();
                modalService.close('zone-media');
                resolve(url ? { url, type: activeTab } : null);
            };

            const btnCancel = document.createElement('button');
            btnCancel.textContent = 'Cancel';
            btnCancel.onclick = () => { modalService.close('zone-media'); resolve(null); };

            actions.appendChild(btnOk);
            actions.appendChild(btnCancel);

            wrap.appendChild(tabs);
            wrap.appendChild(videoPanel);
            wrap.appendChild(imagePanel);
            wrap.appendChild(actions);

            modalService.showCreatedModal('zone-media', wrap, true, {
                'chat-modal-close': () => { modalService.close('zone-media'); resolve(null); }
            }, 'confirm', true);

            setTimeout(() => videoInput.focus(), 50);
        });
    },

    async _onTapRenderZoneClose(element) {
        const uuid = element.id.match(/(^vz_rectangle_)(.+)/)[2];
        this._renderZoneManager.removeZone(uuid);
        this._removeRenderZoneCloseIcon(uuid);
        this._showRenderZoneCounter();
        this._fireEvent('render-zone-removed', { uuid });
    },

    _addRenderZoneCloseElement(uuid) {
        const el = this._createRenderZoneEl('modal-close', `vz_rectangle_${uuid}`);
        this._renderZoneCloseElements.push([el]);
    },

    _removeRenderZoneCloseIcon(uuid) {
        const pair = this._renderZoneCloseElements.find(p => p[0].id === `vz_rectangle_${uuid}`);
        if (!pair) return;
        PannellumAdmin.unbindFloorpointEvents(pair[0]);
        pair[0].parentNode && pair[0].parentNode.removeChild(pair[0]);
        this._renderZoneCloseElements = this._renderZoneCloseElements.filter(p => p[0].id !== `vz_rectangle_${uuid}`);
    },

    _removeAllRenderZoneCloseIcons() {
        const els = [...this._renderZoneCloseElements];
        els.forEach(pair => {
            const m = pair[0].id.match(/(^vz_rectangle_)(.+)/);
            if (m) this._removeRenderZoneCloseIcon(m[2]);
        });
    },

    _createRenderZonePointEl() {
        const el = this._createRenderZoneEl('');
        E3Drag.init(el, () => {}, () => {}, () => {}, () => {}, { threshold: 4 });
        el.style.touchAction = 'none';
        return [el];
    },

    _createRenderZoneEl(classname, id) {
        const el = document.createElement('div');
        el.className = `pnlm-floorpoint ${classname} pnlm-renderzone-point`.trim();
        if (id) {
            el.setAttribute('id', id);
            E3Drag.init(el, () => {}, () => {}, () => {}, this._onTapRenderZoneClose.bind(this, el), { threshold: 4 });
        }
        el.style.touchAction = 'none';
        this._renderContainer.appendChild(el);
        return el;
    },

    _renderRenderZonePoints() {
        this._renderZoneManager.getVerticesSpherCoords().forEach((coords, i) => {
            this._renderHotSpotElement(...coords[0].map(c => c / Pannellum.RADIAN), this._renderZonePoints[i][0], undefined, 'blurPoint');
        });
        this._renderZoneManager.getBoundingCoordVerticesSpherCoords().forEach(({ bountingCoord, uuid }) => {
            const pair = this._renderZoneCloseElements.find(p => p[0].id === `vz_rectangle_${uuid}`);
            if (pair) this._renderHotSpotElement(...bountingCoord[0].map(c => c / Pannellum.RADIAN), pair[0], undefined, 'blurPoint');
        });
    },

    _destroyRenderZonePoint(index) {
        this._renderZonePoints.splice(index, 1)[0].forEach(el => {
            PannellumAdmin.unbindFloorpointEvents(el);
            el.parentNode && el.parentNode.removeChild(el);
        });
    },

    _destroyRenderZonePoints() {
        while (this._renderZonePoints.length) {
            this._destroyRenderZonePoint(this._renderZonePoints.length - 1);
        }
    },

    toggleRenderZoneEditorMode(enable) {
        if (this._renderZoneEditorMode === enable) return;
        this._renderZoneEditorMode = enable;
        this._waitingRenderZonePoint = enable;
        if (enable) {
            this._container.classList.add('render-zone-mode');
            this._renderZoneManager = new RenderZoneManager();
        } else {
            this._container.classList.remove('render-zone-mode');
            this._destroyRenderZonePoints();
            this._removeAllRenderZoneCloseIcons();
        }
        this._render();
    },

    _addRenderZoneListeners() {
        this.on('destroy', () => { this._destroyRenderZonePoints(); this._removeAllRenderZoneCloseIcons(); });
        this.on('load-scene', this.setRenderZoneData.bind(this));
        this.on('get-render-params', params => {
            if (this._renderZoneEditorMode) Object.assign(params, { e3RenderZone: this._renderZoneManager.getCubeVertices() });
        });
        this.on('continue-interacting', this.renderZoneContinueInteracting.bind(this));
        this.on('finish-interacting', this.renderZoneFinishInteracting.bind(this));
        this.on('render', this.renderZoneRender.bind(this));
    },

    _showRenderZoneCounter() {
        const count = this._renderZoneManager.countZones();
        this._fireEvent('render-zone-count', count);
    },

    getRenderZones() {
        return this._renderZoneManager ? this._renderZoneManager.getZonesForSave() : [];
    },

    renderZoneDestroy() {
        if (this._renderZoneManager) this._renderZoneManager.destroy();
    },
};
